import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AlertCircle, Loader2, Lock, Mail } from "lucide-react";
import BrandMark from "@/components/app/BrandMark";
import { useAuth } from "@/lib/auth";
import { formatErr } from "@/lib/api";

const DEMO_ACCOUNTS = [
  { label: "Super Admin (Politeknik Negeri Manado)", email: "admin@greencon.id", password: "Admin#2026" },
  { label: "Admin Perusahaan Pengguna A", email: "admin.sinar@greencon.id", password: "Sinar#2026" },
  { label: "Operator Perusahaan Pengguna A", email: "operator.sinar@greencon.id", password: "Sinar#2026" },
  { label: "Keuangan Perusahaan Pengguna A", email: "keuangan.sinar@greencon.id", password: "Sinar#2026" },
  { label: "Manajer Perusahaan Pengguna A", email: "manajer.sinar@greencon.id", password: "Sinar#2026" },
  { label: "Auditor Perusahaan Pengguna A", email: "auditor.sinar@greencon.id", password: "Sinar#2026" },
  { label: "Admin Perusahaan Pengguna B", email: "admin.tekno@greencon.id", password: "Tekno#2026" },
  { label: "Tim Pengabdian (Politeknik Negeri Manado)", email: "pengabdian@greencon.id", password: "Polimdo#2026" },
];

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const user = await login(email, password);
      toast.success(`Selamat datang, ${user.name}`);
      navigate("/app");
    } catch (err) {
      const msg = formatErr(err);
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const fillDemo = (a) => { setEmail(a.email); setPassword(a.password); };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/app";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-slate-50">
      <div className="hidden lg:block relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0B192C] via-[#052E1C] to-emerald-900" />
        <div className="gc-grain absolute inset-0" />
        <div className="absolute -bottom-16 -left-16 w-96 h-96 rounded-full bg-emerald-500/25 blur-3xl" />
        <div className="relative h-full flex flex-col p-12 text-white">
          <BrandMark variant="light" withInstitution />
          <div className="mt-auto">
            <span className="gc-label !text-emerald-300/80">Sistem Informasi Akuntansi</span>
            <h1 className="font-cabinet text-4xl font-black mt-3 leading-tight">Harga Pokok Produksi Beton Rendah Karbon.</h1>
            <p className="mt-4 text-slate-300 max-w-md">Perhitungan biaya yang akurat, transparan, dan berkelanjutan.</p>
            <div className="mt-4 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-3 py-1.5 text-xs font-semibold text-slate-100 backdrop-blur-sm">
              <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 10v6M2 10l10-5 10 5-10 5z"/><path d="M6 12v5c3 3 9 3 12 0v-5"/></svg>
              Produk Inovasi Politeknik Negeri Manado
            </div>
            <div className="mt-8 grid grid-cols-2 gap-3">
              <div className="rounded-xl border border-white/10 bg-white/5 p-4">
                <div className="text-2xl font-cabinet font-black text-emerald-300">Full Costing</div>
                <div className="text-xs text-slate-300 mt-1">Bahan • Tenaga Kerja • Overhead • Lingkungan</div>
              </div>
              <div className="rounded-xl border border-white/10 bg-white/5 p-4">
                <div className="text-2xl font-cabinet font-black text-emerald-300">Green Accounting</div>
                <div className="text-xs text-slate-300 mt-1">Identifikasi biaya lingkungan sistematis</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-8 lg:p-14">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-6"><BrandMark /></div>
          <span className="gc-label">Masuk</span>
          <h2 className="gc-h1 mt-2">Selamat datang kembali</h2>
          <p className="text-slate-500 mt-2 text-sm">Gunakan akun mitra atau tim pengabdian untuk masuk ke GREENCON HPP.</p>

          <button onClick={googleLogin} className="mt-8 w-full flex items-center justify-center gap-3 rounded-full border border-slate-200 bg-white h-11 text-sm font-medium text-slate-800 hover:bg-slate-50 transition-colors" data-testid="btn-google-login">
            <svg viewBox="0 0 48 48" className="w-5 h-5"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>
            Masuk dengan Google
          </button>

          <div className="my-6 flex items-center gap-3">
            <div className="h-px bg-slate-200 flex-1" />
            <span className="text-xs text-slate-400 uppercase tracking-widest">atau email</span>
            <div className="h-px bg-slate-200 flex-1" />
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="gc-label mb-2 block">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input value={email} onChange={(e) => setEmail(e.target.value)} required type="email" placeholder="anda@perusahaan.com" className="gc-input pl-10" data-testid="input-email" />
              </div>
            </div>
            <div>
              <label className="gc-label mb-2 block">Kata Sandi</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input value={password} onChange={(e) => setPassword(e.target.value)} required type="password" placeholder="••••••••" className="gc-input pl-10" data-testid="input-password" />
              </div>
            </div>
            {error && (
              <div className="flex items-start gap-2 rounded-xl bg-rose-50 border border-rose-200 text-rose-700 text-sm p-3" data-testid="login-error">
                <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" /> {error}
              </div>
            )}
            <button type="submit" disabled={loading} className="w-full gc-btn-primary justify-center h-11" data-testid="btn-login-submit">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Masuk"}
            </button>
          </form>

          <div className="mt-6 rounded-2xl border border-amber-200 bg-amber-50 p-4">
            <div className="text-[11px] font-bold uppercase tracking-widest text-amber-700 mb-2">Akun Demo (DATA DEMO)</div>
            <p className="text-[11px] text-amber-800 mb-2">Perusahaan pengguna di bawah adalah <strong>contoh pengguna sistem</strong>. Sistem GREENCON HPP dimiliki dan dikelola oleh Politeknik Negeri Manado.</p>
            <div className="grid gap-1.5 max-h-40 overflow-y-auto pr-1">
              {DEMO_ACCOUNTS.map((a) => (
                <button key={a.email} type="button" onClick={() => fillDemo(a)} className="flex items-center justify-between text-left text-xs text-slate-700 hover:text-emerald-700 hover:bg-white rounded-lg px-2 py-1.5 transition-colors" data-testid={`demo-${a.email}`}>
                  <span className="font-semibold">{a.label}</span>
                  <span className="text-slate-400 font-mono truncate ml-2">{a.email}</span>
                </button>
              ))}
            </div>
          </div>

          <p className="mt-6 text-xs text-center text-slate-500">
            <Link to="/" className="hover:text-emerald-600">&larr; Kembali ke beranda</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
